import { useCallback, useEffect, useRef, useState } from 'react'
import { getTvRemoteCredentials } from '../lib/pairing'
import { REMOTE_PROTOCOL_VERSION, type RemoteCommand, type RemoteTvState } from '../lib/remoteProtocol'
import { RemoteSocket, type RemoteSocketStatus } from '../lib/remoteSocket'

export interface TvRemoteConnectionState {
  status: RemoteSocketStatus
  controllers: number
}

/**
 * `getSync` is called only when a frame actually carries the snapshot — on
 * connect and on request. Building it eagerly meant reading the whole library,
 * progress and watched history out of storage and size-checking it every time
 * playback advanced, then discarding the result.
 */
export function useTvRemote(
  state: Omit<RemoteTvState, 'revision' | 'updatedAt' | 'sync'>,
  onCommand: (command: RemoteCommand) => void | Promise<void>,
  getSync: () => RemoteTvState['sync'],
) {
  const [credentialsVersion, setCredentialsVersion] = useState(0)
  const [connection, setConnection] = useState<TvRemoteConnectionState>({ status: 'idle', controllers: 0 })
  const socketRef = useRef<RemoteSocket | null>(null)
  const commandRef = useRef(onCommand)
  const syncRef = useRef(getSync)
  const stateRef = useRef(state)
  const publishTimer = useRef(0)
  const revision = useRef(0)
  const seenCommands = useRef<string[]>([])
  const commandQueue = useRef<Promise<void>>(Promise.resolve())
  commandRef.current = onCommand
  syncRef.current = getSync
  stateRef.current = state

  const publish = useCallback((includeSync = false) => {
    window.clearTimeout(publishTimer.current)
    publishTimer.current = 0
    const current = stateRef.current
    const payload: RemoteTvState = {
      ...current,
      // Private sync can be large. Build and send it on connect/request, not on
      // each playback time update that drives the live phone controller.
      sync: includeSync ? syncRef.current() : undefined,
      v: REMOTE_PROTOCOL_VERSION,
      revision: ++revision.current,
      updatedAt: Date.now(),
    }
    socketRef.current?.send({ v: REMOTE_PROTOCOL_VERSION, type: 'state', state: payload }, 0)
  }, [])

  useEffect(() => {
    const changed = () => setCredentialsVersion((value) => value + 1)
    window.addEventListener('vela:remote-credentials', changed)
    return () => window.removeEventListener('vela:remote-credentials', changed)
  }, [])

  useEffect(() => {
    const credentials = getTvRemoteCredentials()
    if (!credentials) {
      socketRef.current?.stop()
      socketRef.current = null
      setConnection({ status: 'idle', controllers: 0 })
      return
    }
    const socket = new RemoteSocket({
      deviceId: credentials.deviceId,
      role: 'tv',
      token: credentials.tvToken,
      onStatus: (status) => {
        setConnection((value) => ({ ...value, status }))
        if (status === 'connected') window.setTimeout(() => publish(true), 0)
      },
      onMessage: (message) => {
        if (message.type === 'presence' || message.type === 'hello') {
          setConnection((value) => ({ ...value, controllers: message.controllers }))
          return
        }
        if (message.type === 'state_request') {
          publish(true)
          return
        }
        if (message.type !== 'command') return
        if (seenCommands.current.includes(message.id)) return
        seenCommands.current = [...seenCommands.current, message.id].slice(-100)
        // Reconnected TVs can receive several queued commands at once. Keep
        // their original order even when metadata/source commands are async.
        commandQueue.current = commandQueue.current
          .then(() => commandRef.current(message.command))
          .catch(() => undefined)
      },
    })
    socketRef.current = socket
    socket.start()
    return () => {
      if (socketRef.current === socket) socketRef.current = null
      socket.stop()
    }
  }, [credentialsVersion, publish])

  useEffect(() => {
    // Playback timeupdate commonly fires faster than 350 ms. Debouncing here
    // postponed every state forever while video was advancing, leaving the
    // phone stuck on the pre-player screen. Throttle instead: the scheduled
    // publish reads stateRef, so it always sends the newest frame.
    if (!publishTimer.current) publishTimer.current = window.setTimeout(() => publish(false), 350)
  }, [publish, state])

  useEffect(() => () => window.clearTimeout(publishTimer.current), [])

  return connection
}
